import { Component, Input, OnInit, OnDestroy, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

import { Subscription } from 'rxjs';
import { tap, filter, switchMap, startWith, pairwise } from 'rxjs/operators';

import { AuthUser } from '@shared/signup/models';
import { Invoice } from '@shared/recurly/models';
import { RecurlyService } from '../../../services';
import { AppIcon, AppIconColor } from '@tablesready/frontend-icons';
import { AuthRestaurantService, SocketService } from '@tablesready/frontend-store';

const MAX_PAGE_SIZE = 5;

@Component({
  selector: 'tablesready-invoices',
  templateUrl: './invoices.component.html',
  styleUrls: ['./invoices.component.scss'],
})
export class InvoicesComponent implements OnInit, OnDestroy {
  @Input() locationSubscriptionId?: string;
  @Input() title = 'Invoice history';
  @Input() timezone: string;
  @Input() isMobile = false;
  invoices: Invoice[] = [];
  invoicesToDisplay: Invoice[] = [];
  icon = AppIcon;
  iconColor = AppIconColor;
  activeInvoicesBtnActive = true;

  pageIndex = 0;
  pageSize = MAX_PAGE_SIZE;
  maxDisplayPages = MAX_PAGE_SIZE;
  private searchQuery: string;

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly recurlyService: RecurlyService,
    private readonly socketService: SocketService,
    private readonly authRestaurantService: AuthRestaurantService,
    @Inject(DOCUMENT) private document: HTMLDocument,
  ) {
  }

  ngOnInit(): void {
    const invoicesSub = this.authRestaurantService.select()
      .pipe(
        startWith(null),
        pairwise(),
        filter(([prev, current]) => this.compareCurrentStateWithPrevious(prev, current)),
        switchMap(() => this.recurlyService.getInvoices()),
        tap(invoices => {
          this.invoices = invoices;

          if (this.locationSubscriptionId !== undefined) {
            this.invoices = this.invoices.filter(invoice => invoice.subscriptionIds.includes(this.locationSubscriptionId));
          }

          this.invoicesToDisplay = this.invoices;
        }),
      )
      .subscribe();

    this.subscriptions.add(invoicesSub);
  }

  toggleDisplayInvoices(partialId: string): void {
    const elements = Array.from(this.document.querySelectorAll(`[id^="${partialId}"]`));

    if (elements.length === 0) {
      return;
    }

    for (const element of elements) {
      const className = partialId.includes('active-invoices') ? 'd-none' : 'd-table-row';
      element.classList.toggle(className);
    }
  }

  search(query: string): void {
    this.searchQuery = query;

    if (query === '') {
      this.invoicesToDisplay = this.invoices;

      return;
    }

    this.invoicesToDisplay = this.invoices.filter(invoice => invoice.number.includes(this.searchQuery));
  }

  compareCurrentStateWithPrevious(prev: AuthUser, current: AuthUser): boolean {
    return current.account !== undefined &&
      (
        prev?.location._id !== current.location._id
        || prev?.location.recurly.subscriptionUuid !== current.location.recurly.subscriptionUuid
        || prev?.location.recurly.subscription_state !== current.location.recurly.subscription_state
        || prev?.location.recurly.planCode !== current.location.recurly.planCode
        || prev?.account.recurly.trial !== current.account.recurly.trial
      );
  }

  downloadInvoice(id: string): void {
    this.subscriptions.add(this.recurlyService.downloadInvoice(id).subscribe());
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  trackBy(index: number): number {
    return index;
  }
}
